import React from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { I } from './icons';
import Navbar from './components/organisms/Navbar';
import { TopicsProvider } from './context/TopicsContext';
import { CourseworkProvider } from './context/CourseworkContext';
import { AuthProvider, useAuth } from './context/AuthContext';

const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const StudentDashboard = React.lazy(() => import('./pages/student/StudentDashboard'));
const LeaderboardPage = React.lazy(() => import('./pages/student/LeaderboardPage'));
const WeeklyReportPage = React.lazy(() => import('./pages/student/WeeklyReportPage'));
const CoursePage = React.lazy(() => import('./pages/student/CoursePage'));
const GradesPage = React.lazy(() => import('./pages/student/GradesPage'));
const ClassroomPage = React.lazy(() => import('./pages/student/ClassroomPage'));
const FeedPage = React.lazy(() => import('./pages/student/FeedPage'));
const AdminOverview = React.lazy(() => import('./pages/admin/AdminOverview'));
const AdminStudents = React.lazy(() => import('./pages/admin/AdminStudents'));
const AdminPending = React.lazy(() => import('./pages/admin/AdminPending'));
const AdminClassroom = React.lazy(() => import('./pages/admin/AdminClassroom'));
const AdminSubmissions = React.lazy(() => import('./pages/admin/AdminSubmissions'));
const DokAssistants = React.lazy(() => import('./pages/dok/DokAssistants'));
const DokGroups = React.lazy(() => import('./pages/dok/DokGroups'));
const DokRoster = React.lazy(() => import('./pages/dok/DokRoster'));
const DokSystem = React.lazy(() => import('./pages/dok/DokSystem'));

/**
 * What shows while a page's chunk is in flight.
 *
 * Deliberately the same empty `.page .page-wide` the guarded routes already use
 * while a stored token is being verified — an empty frame the navbar sits above,
 * rather than a spinner. On a fast connection the chunk arrives within a frame or
 * two, and a spinner that appears and vanishes that quickly reads as a flicker.
 */
const PageFallback = () => <div className="page page-wide" />;

// Design defaults chosen in the redesign review (formerly the tweaks panel).
const THEME = {
  nav: 'pill',
  shade: 'default',
  density: 'compact',
  cards: 'elevated',
  layout: 'split',
  typeScale: 'medium',
};

// The router and the auth provider have to sit above the app, and the app needs
// hooks from both, so App is just the wrapper and AppShell is the real thing.
export default function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}

function AppShell() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, restoring, isDok, logout: endSession } = useAuth();
  // There is no notifications endpoint: the API pushes them over SSE
  // (/admin/adminSSE, /student/studentSSEConnection), which is a later phase.
  // Empty rather than seeded with fakes, so the bell tells the truth.
  const [notifs, setNotifs] = React.useState([]);
  const [toast, setToast] = React.useState(null);
  const toastTimer = React.useRef(null);

  React.useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-nav', THEME.nav);
    root.setAttribute('data-shade', THEME.shade);
    root.setAttribute('data-density', THEME.density);
    root.setAttribute('data-cards', THEME.cards);
    root.setAttribute('data-layout', THEME.layout);
    root.setAttribute('data-type-scale', THEME.typeScale);
  }, []);

  const showToast = (t) => {
    clearTimeout(toastTimer.current);
    setToast(t);
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  };

  // LoginPage signs in through AuthContext and hands the user here purely so we
  // can route them to the right home page.
  const completeAuth = (u) => {
    navigate(u.role === 'admin' ? '/admin' : '/dashboard');
  };

  const logout = () => {
    endSession();
    navigate('/');
    showToast({ kind: 'ok', msg: 'Logged out.' });
  };

  const markAllRead = () => setNotifs((n) => n.map((x) => ({ ...x, read: true })));

  const isPublic = ['/', '/home', '/login', '/register'].includes(pathname);
  const isAdminRoute = pathname.startsWith('/admin');
  // Pages backed by /student/* endpoints, which reject an admin token. Without
  // this guard an admin could open one and see nothing but errors.
  // (/leaderboard, /classroom and /feed use the shared `protect`, so both roles
  // can read them.)
  const isStudentOnlyRoute = ['/dashboard', '/report', '/coursework', '/grades'].includes(pathname);

  React.useEffect(() => {
    // While a stored token is being checked we don't yet know who the user is;
    // redirecting now would bounce a signed-in user to /login on every refresh.
    if (restoring) return;
    if (!user && !isPublic) navigate('/login', { replace: true });
  }, [pathname, user, restoring]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Role mismatches are resolved DURING RENDER, not in an effect.
   *
   * An effect runs after the children have mounted, so an admin opening /report
   * mounted WeeklyReportPage first: it fired /student/getMyWeeklyReport and
   * /student/getQuizTrend, both of which answer 401 "Not authorized as student"
   * for an admin token, and only then did the redirect kick in. The result was a
   * flash of a report page with no figures in it. Returning a redirect from
   * render means the page never mounts and never calls those endpoints.
   */
  const roleRedirect = restoring || !user ? null
    : user.role === 'student' && isAdminRoute ? '/dashboard'
      : user.role === 'admin' && isStudentOnlyRoute ? '/admin'
        : null;

  // Reset scroll to the top whenever the route changes, so a new page never
  // opens already scrolled down from the previous one.
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const hideNav = pathname === '/login' || pathname === '/register';

  // A guarded route shows a skeleton page while the stored token is verified,
  // then either the page or the login form.
  const guarded = (element) => {
    if (restoring) return <div className="page page-wide" />;
    return user ? element : <LoginPage onComplete={completeAuth} onToast={showToast} />;
  };
  const adminOnly = (element) =>
    user && user.role === 'admin' ? element : guarded(<Navigate to="/dashboard" replace />);
  /**
   * The super admin's own pages.
   *
   * They live under /admin/* deliberately: the brief is that the DOK sees the
   * SAME view as an assistant with their extra powers added, so their pages
   * belong in the same section rather than a parallel /dok area — the admin nav
   * stays put and the super-admin menu sits beside it.
   *
   * An assistant who reaches one of these URLs is sent to /admin rather than
   * shown an error: they are a legitimate admin, just not the owner, and every
   * endpoint behind these pages would answer 403 for them anyway.
   */
  const dokOnly = (element) => adminOnly(isDok ? element : <Navigate to="/admin" replace />);

  return (
    <TopicsProvider>
    <CourseworkProvider>
    <div className="app-shell">
      {!hideNav && (
        <Navbar
          variant={THEME.nav}
          user={user}
          notifications={notifs}
          onMarkRead={markAllRead}
          onLogout={logout}
        />
      )}

      <main key={pathname}>
        {/* One boundary for the whole table — see the React.lazy block above.
            It sits INSIDE main so the navbar stays put while a chunk loads,
            rather than the whole shell blinking. */}
        <React.Suspense fallback={<PageFallback />}>
        {roleRedirect ? <Navigate to={roleRedirect} replace /> : (
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/register" element={<RegisterPage onComplete={completeAuth} onToast={showToast} />} />
          <Route path="/login" element={<LoginPage onComplete={completeAuth} onToast={showToast} />} />

          <Route path="/dashboard" element={guarded(user && <StudentDashboard user={user} layout={THEME.layout} />)} />
          <Route path="/leaderboard" element={guarded(<LeaderboardPage />)} />
          <Route path="/grades" element={guarded(<GradesPage />)} />
          <Route path="/report" element={guarded(user && <WeeklyReportPage user={user} />)} />
          <Route path="/coursework" element={guarded(<CoursePage onToast={showToast} />)} />
          <Route path="/homework" element={<Navigate to="/coursework" replace />} />
          <Route path="/classroom" element={guarded(<ClassroomPage onToast={showToast} />)} />
          <Route path="/feed" element={guarded(<FeedPage />)} />

          <Route path="/admin" element={adminOnly(<AdminOverview onNav={navigate} onToast={showToast} />)} />
          <Route path="/admin/students" element={adminOnly(<AdminStudents onToast={showToast} />)} />
          {/* The super admin has no Requests tab (see adminLinksFor in Navbar):
              the queue is always empty for them, and they cannot approve a
              student anyway. Anyone arriving by bookmark or typed URL is sent to
              the queue that IS theirs rather than to a page that can only ever
              say "nothing here". */}
          <Route
            path="/admin/pending"
            element={adminOnly(isDok
              ? <Navigate to="/admin/assistants" replace />
              : <AdminPending onToast={showToast} />)}
          />
          <Route path="/admin/classroom" element={adminOnly(<AdminClassroom onToast={showToast} />)} />
          <Route path="/admin/content" element={<Navigate to="/admin/classroom" replace />} />
          <Route path="/admin/submissions" element={adminOnly(<AdminSubmissions onToast={showToast} />)} />

          {/* Super admin (admin id 1) — see dokOnly above. */}
          <Route path="/admin/assistants" element={dokOnly(<DokAssistants onToast={showToast} />)} />
          <Route path="/admin/groups" element={dokOnly(<DokGroups onToast={showToast} />)} />
          <Route path="/admin/roster" element={dokOnly(<DokRoster onToast={showToast} />)} />
          <Route path="/admin/system" element={dokOnly(<DokSystem onToast={showToast} />)} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        )}
        </React.Suspense>
      </main>

      {toast && (
        <div className={`toast ${toast.kind || ''}`}>
          {toast.kind === 'ok' && <I.Check size={16} />}
          {toast.kind === 'error' && <I.Close size={16} />}
          {toast.msg}
        </div>
      )}
    </div>
    </CourseworkProvider>
    </TopicsProvider>
  );
}
